import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Calendar, MapPin, Clock, Heart } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { useLanguage } from "../context/LanguageContext";
import { usePageContent } from "../hooks/usePageContent";
import { API_URL as API } from "../config";

const GOFUNDME_URL = "https://gofund.me/274183352";

const useScrollReveal = (threshold = 0.1) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const currentRef = ref.current;
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setIsVisible(true); }, { threshold, rootMargin: '50px' });
    if (currentRef) observer.observe(currentRef);
    return () => { if (currentRef) observer.unobserve(currentRef); };
  }, [threshold]);
  return { ref, isVisible };
};

const AnimatedSection = ({ children, className = "", animation = "fly-up", delay = 0 }) => {
  const { ref, isVisible } = useScrollReveal(0.1);
  const baseTransform = { 'fly-left': 'translateX(-80px)', 'fly-right': 'translateX(80px)', 'fly-up': 'translateY(60px)', 'fly-down': 'translateY(-60px)', 'zoom': 'scale(0.8)' };
  const style = { opacity: isVisible ? 1 : 0, transform: isVisible ? 'translateX(0) translateY(0) scale(1)' : baseTransform[animation] || 'translateY(60px)', transition: `opacity 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${delay}ms, transform 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${delay}ms` };
  return <div ref={ref} className={className} style={style}>{children}</div>;
};

const InteractiveCard = ({ children, className = "" }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const cardRef = useRef(null);
  const handleMouseMove = (e) => { if (!cardRef.current) return; const rect = cardRef.current.getBoundingClientRect(); setMousePos({ x: (e.clientX - rect.left - rect.width / 2) / 20, y: (e.clientY - rect.top - rect.height / 2) / 20 }); };
  return (
    <div ref={cardRef} className={`interactive-card hover-glow ${className}`} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => { setIsHovered(false); setMousePos({ x: 0, y: 0 }); }} onMouseMove={handleMouseMove}
      style={{ transform: isHovered ? `perspective(1000px) rotateY(${mousePos.x}deg) rotateX(${-mousePos.y}deg) translateY(-10px)` : 'perspective(1000px) rotateY(0) rotateX(0)' }}>
      {children}
    </div>
  );
};

const EventsPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const { lang, t } = useLanguage();
  const { get } = usePageContent("events");

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await axios.get(`${API}/api/events`);
        if (response.data.length === 0) {
          await axios.post(`${API}/api/events/seed`);
          const seeded = await axios.get(`${API}/api/events`);
          setEvents(seeded.data);
        } else {
          setEvents(response.data);
        }
      } catch (e) {
        console.error("Failed to fetch events:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  const title = lang === "es" ? "Eventos de Recaudacion | Curious 123" : "Fundraising Events | Curious 123 Youth Soccer";
  const desc = lang === "es" ? "Acompananos en eventos para apoyar el futbol juvenil en San Juan del Sur, Nicaragua." : "Join our upcoming fundraising events to support youth soccer in San Juan del Sur, Nicaragua.";

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={desc} />
        <link rel="canonical" href="https://curious123.org/events" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={desc} />
        <meta property="og:url" content="https://curious123.org/events" />
        <meta property="og:image" content="https://customer-assets.emergentagent.com/job_a9038138-35cf-4d7a-8b3b-ccb6b5c034cc/artifacts/7e22fcgr_image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Curious 123" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={desc} />
        <meta name="twitter:image" content="https://customer-assets.emergentagent.com/job_a9038138-35cf-4d7a-8b3b-ccb6b5c034cc/artifacts/7e22fcgr_image.png" />
      </Helmet>
      <section className="pt-32 pb-20 bg-gradient-to-b from-[#F0F9FF] to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection animation="fly-up" className="text-center max-w-4xl mx-auto">
            <span className="inline-block bg-[#87CEEB] text-black px-4 py-2 rounded-full text-sm font-semibold mb-6 uppercase tracking-wider">{t.events.badge}</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-black leading-tight mb-6 font-['Kanit']">{get("hero_title", t.events.title)}</h1>
            <p className="text-lg text-gray-700">{get("hero_subtitle", t.events.subtitle)}</p>
          </AnimatedSection>
        </div>
      </section>
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-12">
              <div className="w-12 h-12 border-4 border-[#87CEEB] border-t-transparent rounded-full animate-spin mx-auto"></div>
              <p className="mt-4 text-gray-600">{t.events.loading}</p>
            </div>
          ) : events.length === 0 ? (
            <div className="text-center py-12 bg-[#F0F9FF] rounded-2xl animate-fade-in">
              <Calendar className="w-16 h-16 text-[#87CEEB] mx-auto mb-4 animate-float" />
              <p className="text-gray-600 text-lg">{t.events.noEvents}</p>
              <p className="text-gray-500">{t.events.noEventsDesc}</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {events.map((event, index) => (
                <Link to={`/events/${event.id}`} key={event.id} className="animate-fade-in block" style={{ animationDelay: `${index * 150}ms` }} data-testid={`event-card-${index}`}>
                  <InteractiveCard className="h-full">
                    <Card className="bg-white border-gray-100 shadow-sm rounded-2xl overflow-hidden h-full">
                      {event.image_url && (
                        <div className="relative h-48 overflow-hidden">
                          <img src={event.image_url} alt={event.title} className="w-full h-full object-cover transition-transform duration-500 hover:scale-110" />
                          <div className="absolute top-4 left-4 bg-[#87CEEB] text-black px-3 py-2 rounded-lg">
                            <p className="font-bold text-sm font-['Kanit']">{new Date(event.date).toLocaleDateString(lang === 'es' ? 'es-NI' : 'en-US', { month: 'short', day: 'numeric' })}</p>
                          </div>
                        </div>
                      )}
                      <CardHeader><CardTitle className="text-xl font-bold text-black font-['Kanit']">{event.title}</CardTitle></CardHeader>
                      <CardContent className="space-y-4">
                        <CardDescription className="text-gray-600">{event.description}</CardDescription>
                        <div className="space-y-2 text-sm text-gray-500">
                          <div className="flex items-center gap-2 hover:text-[#87CEEB] transition-colors"><Clock className="w-4 h-4 text-[#87CEEB]" /><span>{event.time}</span></div>
                          <div className="flex items-center gap-2 hover:text-[#87CEEB] transition-colors"><MapPin className="w-4 h-4 text-[#87CEEB]" /><span>{event.location}</span></div>
                        </div>
                      </CardContent>
                    </Card>
                  </InteractiveCard>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
      <section className="py-20 bg-[#F0F9FF]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <AnimatedSection animation="zoom">
            <h2 className="text-3xl font-bold text-black mb-6 font-['Kanit']">{t.events.cantMake}</h2>
            <p className="text-lg text-gray-600 mb-8">{t.events.cantMakeDesc}</p>
            <a href={GOFUNDME_URL} target="_blank" rel="noopener noreferrer">
              <Button className="donate-btn btn-shine rounded-full px-10 py-4 text-lg font-bold uppercase tracking-wider animate-pulse-glow"><Heart className="w-5 h-5 mr-2" />{t.events.donateBtn}</Button>
            </a>
          </AnimatedSection>
        </div>
      </section>
    </>
  );
};

export default EventsPage;
